import { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';

interface AuditRow {
  id: string;
  actor_user_id: string | null;
  actor_role: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  occurred_at: string;
}

export function AuditLogPage() {
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [entityId, setEntityId] = useState('');
  const [rows, setRows] = useState<AuditRow[] | null>(null);

  function load() {
    const params = new URLSearchParams();
    if (action) params.set('action', action);
    if (entityType) params.set('entity_type', entityType);
    if (entityId) params.set('entity_id', entityId);
    api<{ data: AuditRow[] }>(
      `/admin/audit-logs${params.toString() !== '' ? '?' + params.toString() : ''}`,
    )
      .then((res) => setRows(res.data))
      .catch(() => setRows([]));
  }
  // Deliberate run-once-on-mount fetch; filters trigger an explicit
  // reload via the form, not via the dependency array.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-black tracking-tight">Audit log</h1>
        <p className="text-sm text-muted-foreground">Filter platform-wide actor / entity events.</p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-3 sm:grid-cols-[1fr_1fr_1fr_auto]"
            onSubmit={(e) => {
              e.preventDefault();
              load();
            }}
          >
            <Field
              label="Action"
              value={action}
              onChange={setAction}
              placeholder="admin.user.suspend"
            />
            <Field
              label="Entity type"
              value={entityType}
              onChange={setEntityType}
              placeholder="user / agency / cv_document"
            />
            <Field
              label="Entity id (ULID)"
              value={entityId}
              onChange={setEntityId}
              placeholder="01H…"
            />
            <div className="flex items-end">
              <Button type="submit">Apply</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{rows?.length ?? 0} rows</CardTitle>
        </CardHeader>
        <CardContent>
          {rows === null && <div className="h-32 animate-pulse rounded-2xl bg-muted" />}
          {rows?.length === 0 && (
            <p className="text-sm text-muted-foreground">No matching events.</p>
          )}
          {rows !== null && rows.length > 0 && (
            <ul className="space-y-2">
              {rows.map((r) => (
                <li key={r.id} className="rounded-2xl border border-border p-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge>{r.action}</Badge>
                    <span className="text-muted-foreground">{r.entity_type}</span>
                    {r.entity_id !== null && (
                      <code className="font-mono text-xs">{r.entity_id}</code>
                    )}
                    <span className="ml-auto text-xs text-muted-foreground">
                      {formatDateTime(r.occurred_at)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    actor: {r.actor_user_id ?? '—'} ({r.actor_role ?? '—'})
                  </p>
                  {(r.before !== null || r.after !== null) && (
                    <pre className="mt-2 overflow-x-auto rounded-md bg-muted p-2 text-xs">
                      {JSON.stringify({ before: r.before, after: r.after }, null, 2)}
                    </pre>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function formatDateTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}
